import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Gauge, Calculator, Users, BarChart3, FileText, Sparkles, ArrowRight, Check, Menu, X, Star, Zap, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import LanguagePicker from '../components/LanguagePicker';
import { useLanguage } from '../context/LanguageContext';

const HERO_IMAGE = "https://images.unsplash.com/photo-1772235204577-7edbe5f68d6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYXIlMjBkZWFsZXJzaGlwfGVufDB8fHx8MTc3NDcyNTM1OHww&ixlib=rb-4.1.0&q=85";
const DEALERSHIP_IMAGE = "https://images.unsplash.com/photo-1774047366640-29bf407f8667?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjYXIlMjBkZWFsZXJzaGlwfGVufDB8fHx8MTc3NDcyNTM1OHww&ixlib=rb-4.1.0&q=85";
const SALVAGE_IMAGE = "https://images.pexels.com/photos/9783997/pexels-photo-9783997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DEALER_AERIAL = "https://images.pexels.com/photos/395537/pexels-photo-395537.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const features = [
  { icon: Car, title: "Inventory Management", desc: "Track and manage your entire vehicle inventory with powerful tools", color: "from-blue-500 to-cyan-500" },
  { icon: Gauge, title: "VIN Decoder", desc: "Instantly decode VINs for complete vehicle specifications", color: "from-purple-500 to-blue-500" },
  { icon: Calculator, title: "Profit Calculator", desc: "Calculate margins, ROI, and maximize every deal", color: "from-green-500 to-emerald-500" },
  { icon: Users, title: "Lead Tracking", desc: "Never miss a customer with smart CRM tools", color: "from-orange-500 to-amber-500" },
  { icon: BarChart3, title: "Sales Analytics", desc: "Real-time insights to supercharge your business", color: "from-pink-500 to-rose-500" },
  { icon: FileText, title: "Document Storage", desc: "Store all compliance documents securely in the cloud", color: "from-indigo-500 to-purple-500" },
];

const plans = [
  { 
    id: 'starter',
    name: "Starter", 
    price: 22.99, 
    features: ["Up to 15 vehicle listings", "Basic VIN decoder", "Profit calculator", "Bill of Sale generation", "Email support"],
    popular: false,
    color: "blue"
  },
  { 
    id: 'professional',
    name: "Professional", 
    price: 59.99, 
    features: ["Up to 50 vehicle listings", "Advanced VIN decoder", "Expense & repair tracking", "Customer CRM", "Invoice & document templates", "Priority support"],
    popular: true,
    color: "purple"
  },
  { 
    id: 'dealer_pro',
    name: "Dealer Pro", 
    price: 99.99, 
    features: ["Unlimited listings", "Wakzy Check history reports", "Full CRM & analytics", "All document templates", "Image Pro Enterprise (FREE)", "Phone & email support"],
    popular: false,
    color: "cyan"
  },
  { 
    id: 'enterprise',
    name: "Enterprise", 
    price: 149.99, 
    features: ["Everything in Dealer Pro", "AI Document Scanner (OCR)", "Bulk document generation", "Smart VIN extraction", "Dedicated account manager", "API access & white-label"],
    popular: false,
    color: "gold"
  },
];

const stats = [
  { value: "500+", label: "Active Dealers" },
  { value: "$2M+", label: "Monthly Sales" },
  { value: "10K+", label: "Vehicles Listed" },
  { value: "98%", label: "Satisfaction Rate" },
];

export default function LandingPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="/wakzy-logo.png" 
                alt="Wakzy Auto Tech" 
                className="h-14 w-auto"
              />
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/pricing" className="nav-link" data-testid="nav-pricing">
                {t('landing.pricing')}
              </Link>
              <Link to="/sell-junk-car" className="nav-link" data-testid="nav-sell-junk">
                Sell Junk Car
              </Link>
              <LanguagePicker />
              {user ? (
                <Button 
                  onClick={() => navigate(user.role === 'buyer' ? '/buyer' : '/dashboard')}
                  className="bg-gradient-to-r from-[#0052FF] to-[#00D4FF] hover:opacity-90 text-white px-6 py-2 rounded-xl shadow-lg shadow-blue-500/25"
                  data-testid="nav-dashboard-btn"
                >
                  {t('dashboard.title')}
                </Button>
              ) : (
                <>
                  <Link to="/login" className="nav-link" data-testid="nav-login">
                    {t('auth.signIn')}
                  </Link>
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-gradient-to-r from-[#0052FF] to-[#00D4FF] hover:opacity-90 text-white px-6 py-2 rounded-xl shadow-lg shadow-blue-500/25"
                    data-testid="nav-get-started-btn"
                  >
                    {t('landing.getStarted')}
                  </Button>
                </>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-gray-100" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 mt-4 animate-fade-in">
              <div className="flex flex-col gap-4">
                <Link to="/pricing" className="nav-link">Pricing</Link>
                <Link to="/sell-junk-car" className="nav-link">Sell Junk Car</Link>
                {user ? (
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    className="bg-gradient-to-r from-[#0052FF] to-[#00D4FF] text-white w-full rounded-xl"
                  >
                    Dashboard
                  </Button>
                ) : (
                  <>
                    <Link to="/login" className="nav-link">Sign In</Link>
                    <Button 
                      onClick={() => navigate('/register')}
                      className="bg-gradient-to-r from-[#0052FF] to-[#00D4FF] text-white w-full rounded-xl"
                    >
                      Get Started Free
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 hero-gradient hero-pattern overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Modern car dealership" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/60 to-gray-900/90" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6 animate-fade-in">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-white/90">{t('landing.trustedBy') || 'Trusted by 500+ dealers nationwide'}</span>
            </div>
            
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight mb-6 animate-fade-in animate-delay-100"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              data-testid="hero-title"
            >
              {t('landing.hero')}
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed animate-fade-in animate-delay-200 max-w-2xl">
              {t('landing.heroSub')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animate-delay-300">
              <Button 
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-[#0052FF] to-[#00D4FF] hover:opacity-90 text-white text-lg px-8 py-6 rounded-xl shadow-2xl shadow-blue-500/30 group"
                data-testid="hero-cta-btn"
              >
                {t('landing.getStarted')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="border-2 border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl backdrop-blur-sm"
                data-testid="hero-pricing-btn"
              >
                {t('landing.viewPricing')}
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-16 animate-fade-in animate-delay-400">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 font-medium text-sm mb-4">
              <Star className="w-4 h-4" />
              POWERFUL FEATURES
            </div>
            <h2 
              className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-4"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              data-testid="features-title"
            >
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-[#0052FF] to-[#00D4FF] bg-clip-text text-transparent">
                Dominate
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful tools designed specifically for independent dealerships
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="feature-card animate-fade-in"
                style={{ animationDelay: `${idx * 100}ms` }}
                data-testid={`feature-card-${idx}`}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-6 shadow-lg`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dealer SaaS Section */}
      <section className="py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 text-purple-600 font-medium text-sm mb-4">
                <Shield className="w-4 h-4" />
                DEALER SAAS PLATFORM
              </div>
              <h2 
                className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Manage Your Inventory{' '}
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Like a Pro
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our cloud-based dealer management system is designed specifically for independent dealerships. Track inventory, decode VINs, calculate profits, and generate listings automatically.
              </p>
              <ul className="space-y-4 mb-8">
                {["Vehicle inventory management", "Instant VIN decoding", "AI-powered descriptions", "Profit margin calculator", "Sales analytics dashboard"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-4 animate-slide-in" style={{ animationDelay: `${idx * 100}ms` }}>
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:opacity-90 text-white px-8 py-3 rounded-xl shadow-lg shadow-purple-500/25 group"
                data-testid="dealer-saas-cta"
              >
                Start Managing Inventory
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-3xl blur-3xl" />
              <img 
                src={DEALERSHIP_IMAGE} 
                alt="Modern dealership showroom" 
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Junk Car Marketplace Section */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-50" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-3xl blur-3xl" />
              <img 
                src={SALVAGE_IMAGE} 
                alt="Salvage yard aerial view" 
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 text-cyan-400 font-medium text-sm mb-4">
                <Zap className="w-4 h-4" />
                JUNK CAR MARKETPLACE
              </div>
              <h2 
                className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Connect Sellers with Buyers{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Instantly
                </span>
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Our marketplace connects vehicle owners with scrap yards, recyclers, and salvage dealers. Submit your junk car and receive competitive offers within hours.
              </p>
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">$40</div>
                  <div className="text-gray-400">Avg. lead fee</div>
                </div>
                <div className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">24hrs</div>
                  <div className="text-gray-400">Response time</div>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/sell-junk-car')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 text-white px-8 py-3 rounded-xl shadow-lg shadow-cyan-500/25 group"
                data-testid="junk-car-cta"
              >
                Sell Your Junk Car
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Dealer Network Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 text-orange-600 font-medium text-sm mb-4">
                <Users className="w-4 h-4" />
                DEALER NETWORK
              </div>
              <h2 
                className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Expand Your Business with{' '}
                <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                  Quality Leads
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Join our network of independent dealers, scrap yards, and salvage buyers. Get access to a steady stream of qualified leads in your area.
              </p>
              <Button 
                onClick={() => navigate('/register?role=buyer')}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white px-8 py-3 rounded-xl shadow-lg shadow-orange-500/25 group"
                data-testid="dealer-network-cta"
              >
                Join the Network
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-3xl blur-3xl" />
              <img 
                src={DEALER_AERIAL} 
                alt="Dealer lot aerial view" 
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Dealer Image Pro Showcase */}
      <section className="py-24 bg-[#050914] relative overflow-hidden" data-testid="image-pro-section">
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #00F0FF 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-400 font-medium text-sm mb-4 border border-cyan-500/20">
              <Sparkles className="w-4 h-4" />
              NEW: DEALER IMAGE PRO
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              AI-Powered Vehicle Photography
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Professional dealership photos in seconds. Background swap, AI enhancement, social media listings, animated reels, and voice-over commercials — all powered by AI.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { title: "AI Background Swap", desc: "Remove & replace backgrounds with luxury showrooms, outdoor lots, or custom scenes", icon: "wand", color: "from-cyan-500 to-blue-500" },
              { title: "Smart Enhancement", desc: "Auto paint shine, color correction, rim detail, tire black restore — one click", icon: "sparkle", color: "from-violet-500 to-purple-500" },
              { title: "Social Media Ready", desc: "Generate Instagram, Facebook, TikTok listings with AI captions & hashtags", icon: "share", color: "from-pink-500 to-rose-500" },
              { title: "AI Damage Detection", desc: "Scan for scratches, dents, paint defects — get annotated inspection reports", icon: "shield", color: "from-amber-500 to-orange-500" },
              { title: "Animated Car Reels", desc: "Cinematic pan, showroom spin, night drive — 6 styles powered by Sora 2", icon: "film", color: "from-emerald-500 to-teal-500" },
              { title: "Voice-Over Commercials", desc: "Generate professional dealer commercials with 6 HD AI voices", icon: "mic", color: "from-blue-500 to-indigo-500" },
            ].map((f, i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/30 transition-all group">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-3 bg-slate-800/80 border border-slate-700 rounded-2xl px-8 py-4 mb-6">
              <div className="text-left">
                <p className="text-sm text-slate-400">Image Pro Add-on starts at</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-white">$9</span>
                  <span className="text-slate-400">/mo</span>
                </div>
              </div>
              <div className="w-px h-12 bg-slate-700" />
              <div className="text-left">
                <p className="text-sm text-cyan-400 font-semibold">Included FREE</p>
                <p className="text-xs text-slate-400">with Dealer Pro plan</p>
              </div>
            </div>
            <div>
              <Button
                onClick={() => navigate(user ? '/dashboard' : '/register')}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-3 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:shadow-[0_0_30px_rgba(0,240,255,0.5)] text-base font-bold"
                data-testid="image-pro-cta"
              >
                Try Image Pro Free <ArrowRight className="w-5 h-5 ml-2 inline" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white" id="pricing">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 text-green-600 font-medium text-sm mb-4">
              <Sparkles className="w-4 h-4" />
              SIMPLE PRICING
            </div>
            <h2 
              className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-4"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              data-testid="pricing-title"
            >
              Plans That{' '}
              <span className="bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">
                Scale With You
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start free, upgrade when you're ready
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {plans.map((plan, idx) => (
              <div 
                key={idx}
                className={`relative rounded-3xl p-8 transition-all duration-300 ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-[#0052FF] to-[#0043D1] text-white scale-105 shadow-2xl shadow-blue-500/30' 
                    : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-xl'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-cyan-400 to-blue-400 text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wide shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 
                    className={`text-2xl font-bold mb-4 ${plan.popular ? 'text-white' : 'text-gray-900'}`}
                    style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
                  >
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-5xl font-black ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                      ${plan.price}
                    </span>
                    <span className={plan.popular ? 'text-white/80' : 'text-gray-500'}>/month</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        plan.popular ? 'bg-white/20' : 'bg-green-100'
                      }`}>
                        <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-green-600'}`} />
                      </div>
                      <span className={plan.popular ? 'text-white/90' : 'text-gray-600'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/register')}
                  className={`w-full h-12 font-semibold rounded-xl ${
                    plan.popular 
                      ? 'bg-white text-[#0052FF] hover:bg-gray-100' 
                      : 'bg-gradient-to-r from-[#0052FF] to-[#00D4FF] text-white hover:opacity-90'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Features Section */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-r from-purple-500 to-cyan-500 mb-8 animate-pulse-glow">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white font-medium text-sm mb-4">
            AI-POWERED
          </div>
          <h2 
            className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Let AI Write Your{' '}
            <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Perfect Listings
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto">
            Our AI-powered tools automatically generate professional vehicle descriptions and provide market-based pricing suggestions to help you sell faster.
          </p>
          <Button 
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:opacity-90 text-white px-8 py-4 rounded-xl shadow-2xl shadow-purple-500/30 group text-lg"
            data-testid="ai-cta"
          >
            Try AI Features Free
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#0052FF] via-[#0043D1] to-[#7C3AED] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 
            className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Ready to Transform Your Dealership?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join hundreds of independent dealers already using Wakzy Auto Tech.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-[#0052FF] hover:bg-gray-100 text-lg px-10 py-6 rounded-xl shadow-2xl group"
            data-testid="final-cta-btn"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="/wakzy-logo-dark.png" 
                alt="Wakzy Auto Tech" 
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-400 leading-relaxed">
                The modern operating system for independent automotive dealers.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Products</h4>
              <ul className="space-y-3 text-gray-400">
                <li><Link to="/pricing" className="hover:text-cyan-400 transition-colors">Dealer SaaS</Link></li>
                <li><Link to="/sell-junk-car" className="hover:text-cyan-400 transition-colors">Junk Car Marketplace</Link></li>
                <li><Link to="/register?role=buyer" className="hover:text-cyan-400 transition-colors">Dealer Network</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Company</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-cyan-400 transition-colors">About</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Legal</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Wakzy Auto Tech. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
